import { apiClient, API_BASE_URL } from './../client';
import { API_ENDPOINTS } from './../constant/endpoints';
import type { IAccessTokenResponse, IApiResponse } from './../types';
import type {
    ILoginRequest,
    ILoginResponse,
    ILoginViaInvitationRequest,
    IRegisterRequest,
    IRegisterResponse,
    IRegisterViaInvitationRequest,
    IForgotPasswordRequest,
    IResetPasswordRequest,
} from '@/features/auth/interfaces';
import {
    buildOAuthStartUrl,
    type IOAuthStartParams,
    type OAuthProvider,
} from '@/features/auth/oauth-start-url';

export const login = (data: ILoginRequest) =>
    apiClient
        .post<IApiResponse<ILoginResponse>>(API_ENDPOINTS.AUTH.LOGIN, data)
        .then((r) => r.data.data);

export const register = (data: IRegisterRequest) =>
    apiClient
        .post<IApiResponse<IRegisterResponse>>(API_ENDPOINTS.AUTH.REGISTER, data)
        .then((r) => r.data.data);

export const registerViaInvitation = (data: IRegisterViaInvitationRequest) =>
    apiClient
        .post<IApiResponse<IRegisterResponse>>(API_ENDPOINTS.AUTH.REGISTER_VIA_INVITATION, data)
        .then((r) => r.data.data);

/**
 * Sign an existing account in *and* accept its invitation. Goes through
 * `apiClient` rather than the public client because the response sets the
 * httpOnly refresh cookie, which needs `withCredentials`.
 */
export const loginViaInvitation = (data: ILoginViaInvitationRequest) =>
    apiClient
        .post<IApiResponse<ILoginResponse>>(API_ENDPOINTS.AUTH.LOGIN_VIA_INVITATION, data)
        .then((r) => r.data.data);

export const logout = () =>
    apiClient.post<IApiResponse<null>>(API_ENDPOINTS.AUTH.LOGOUT).then((r) => r.data.data);

/**
 * Restore a session from the httpOnly refresh cookie alone. The cookie is
 * shared by every tab of the browser, so a new tab whose per-tab token store is
 * empty can still sign itself back in. `/auth/*` calls are never retried by the
 * 401 interceptor, so a missing or expired cookie simply rejects.
 */
export const refreshSession = () =>
    apiClient
        .post<IApiResponse<IAccessTokenResponse>>(API_ENDPOINTS.AUTH.REFRESH)
        .then((r) => r.data.data.accessToken);

export const forgotPassword = (data: IForgotPasswordRequest) =>
    apiClient
        .post<IApiResponse<null>>(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, data)
        .then((r) => r.data.data);

export const resetPassword = (data: IResetPasswordRequest) =>
    apiClient
        .post<IApiResponse<null>>(API_ENDPOINTS.AUTH.RESET_PASSWORD, data)
        .then((r) => r.data.data);

/**
 * Is a reset link still usable? Lets the reset screen say so on open instead of
 * showing a form that can only fail. POST (not GET) keeps the password-
 * equivalent token out of access logs and `Referer` headers.
 */
export const verifyResetToken = (token: string) =>
    apiClient
        .post<IApiResponse<{ isValid: boolean }>>(API_ENDPOINTS.AUTH.VERIFY_RESET_TOKEN, { token })
        .then((r) => r.data.data);

/**
 * Absolute URL that starts a Google/Microsoft sign-in. A full-page navigation
 * (the API sets its CSRF-binding cookie and 302s to the provider), so it is
 * rendered as a link rather than requested through `apiClient`.
 */
export const oauthStartUrl = (provider: OAuthProvider, params: IOAuthStartParams): string =>
    buildOAuthStartUrl(API_BASE_URL, provider, params);

/**
 * Swaps the single-use ticket from the OAuth callback fragment for a session.
 * Same response as `login` — and, like it, goes through `apiClient` because the
 * response sets the httpOnly refresh cookie, which needs `withCredentials`.
 */
export const oauthExchange = (ticket: string) =>
    apiClient
        .post<IApiResponse<ILoginResponse>>(API_ENDPOINTS.AUTH.OAUTH_EXCHANGE, { ticket })
        .then((r) => r.data.data);
